import { Alert, Button, Input, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantDatabase } from '../redux';
import {
  variantDatabaseFormValidationSchema,
  variantDataBaseUpdateFormValidationSchema,
} from '../validation_schemas/variant_database_validation_schema';
import { useVariantDatabaseModalLogic } from './hooks';

const { TextArea } = Input;

type VariantDatabaseModalProps = {
  onClose: () => void;
  variant_id?: string;
  existingDatabase?: VariantDatabase | null;
};

const VariantDatabaseModal = ({
  onClose,
  variant_id,
  existingDatabase,
}: VariantDatabaseModalProps) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isVariantDatabaseModalLoading,
    isEditMode,
    databaseOptions,
    classificationOptions,
    modalOpts,
    editInitialValues,
    createInitialValues,
    commentEditorState,
    setCommentEditorState,
    getIsSaveDisabled,
    handleFormSubmit,
    handleUpdateDatabase,
  } = useVariantDatabaseModalLogic({ onClose, variant_id, existingDatabase });

  // If in edit mode, pre-fill the form with existing database data
  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isVariantDatabaseModalLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isEditMode ? (
        <Formik
          initialValues={editInitialValues}
          validationSchema={variantDataBaseUpdateFormValidationSchema}
          onSubmit={handleUpdateDatabase}
        >
          {({
            errors,
            touched,
            handleChange,
            values,
            handleSubmit,
            handleBlur,
            isValid,
            dirty,
            setFieldValue,
          }) => {
            // Standardized disable logic via central getIsSaveDisabled
            const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

            return (
              <form onSubmit={handleSubmit}>
                <div className="c-form-field">
                  <div className="c-form-field__label">Database:</div>
                  <div className="c-form-field__wrapper">
                    <Input
                      value={existingDatabase?.database}
                      disabled
                      className="h-proband-modal__input-field-disabled-highlight"
                    />
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Classification:</div>
                  <div className="c-form-field__wrapper">
                    <Select
                      value={values.classification}
                      onChange={(v) => setFieldValue('classification', v)}
                      onBlur={handleBlur}
                      className="h-proband-modal__select-field"
                      options={classificationOptions}
                      placeholder="Choose classification for variant"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={!canInteract}
                      status={!canInteract ? 'error' : undefined}
                    />
                    <div className="c-form-field__error">
                      {errors.classification && touched.classification
                        ? errors.classification
                        : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Evidence:</div>
                  <div className="c-form-field__wrapper">
                    <TextArea
                      name="evidence"
                      value={values.evidence}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder=""
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      readOnly={!canInteract}
                    />
                    <div className="c-form-field__error">
                      {errors.evidence && touched.evidence
                        ? errors.evidence
                        : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Comment:</div>
                  <div className="c-form-field__wrapper">
                    <RichTextEditor
                      name="comment"
                      editorState={commentEditorState}
                      onChange={(newState) => {
                        setCommentEditorState(newState);
                        const htmlContent = editorStateToHtml(newState);
                        void setFieldValue('comment', htmlContent);
                      }}
                      onBlur={() => handleBlur('comment')}
                      placeholder=""
                      label=""
                      readOnly={!canInteract}
                    />
                    <div className="c-form-field__error">
                      {errors.comment && touched.comment
                        ? (errors.comment as any)
                        : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="h-proband-modal__form-submit-btn"
                    // NEW: vereinheitlichte Disable-Logik
                    disabled={isSaveDisabled}
                  >
                    Save changes
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      ) : (
        <Formik
          initialValues={createInitialValues}
          onSubmit={handleFormSubmit}
          validationSchema={variantDatabaseFormValidationSchema}
        >
          {({
            errors,
            handleChange,
            values,
            handleSubmit,
            handleBlur,
            isValid,
            dirty,
            setFieldValue,
          }) => {
            // Standardized disable logic via central getIsSaveDisabled
            const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

            return (
              <form onSubmit={handleSubmit}>
                <div className="c-form-field">
                  <div className="c-form-field__label">Database:</div>
                  <div className="c-form-field__wrapper">
                    <Select
                      value={values.database_id}
                      onChange={(v) => setFieldValue('database_id', v)}
                      onBlur={handleBlur}
                      className="h-proband-modal__select-field"
                      options={databaseOptions}
                      placeholder="Select a database from the list"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={!canInteract}
                      status={!canInteract ? 'error' : undefined}
                    />
                    <div className="c-form-field__error">
                      {errors.database_id ? errors.database_id : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Classification:</div>
                  <div className="c-form-field__wrapper">
                    <Select
                      value={values.classification}
                      onChange={(v) => setFieldValue('classification', v)}
                      onBlur={handleBlur}
                      className="h-proband-modal__select-field"
                      options={classificationOptions}
                      placeholder="Choose classification for variant"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={!canInteract}
                      status={!canInteract ? 'error' : undefined}
                    />
                    <div className="c-form-field__error">
                      {errors.classification
                        ? errors.classification
                        : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Evidence:</div>
                  <div className="c-form-field__wrapper">
                    <TextArea
                      name="evidence"
                      value={values.evidence}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder=""
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      readOnly={!canInteract}
                    />
                    <div className="c-form-field__error">
                      {errors.evidence ? errors.evidence : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form-field">
                  <div className="c-form-field__label">Comment:</div>
                  <div className="c-form-field__wrapper">
                    <RichTextEditor
                      name="comment"
                      editorState={commentEditorState}
                      onChange={(newState) => {
                        setCommentEditorState(newState);
                        const htmlContent = editorStateToHtml(newState);
                        void setFieldValue('comment', htmlContent);
                      }}
                      onBlur={() => handleBlur('comment')}
                      placeholder=""
                      label=""
                      readOnly={!canInteract}
                    />
                    <div className="c-form-field__error">
                      {errors.comment ? (errors.comment as any) : undefined}
                    </div>
                  </div>
                </div>

                <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="h-proband-modal__form-submit-btn"
                    // NEW: vereinheitlichte Disable-Logik
                    disabled={isSaveDisabled}
                  >
                    Save variant database
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default VariantDatabaseModal;
